import React, { useState, useEffect } from "react";
import {
  Modal,
  Text,
  Stack,
  Button,
  Alert,
  Group,
  Box,
  TextInput,
  Checkbox,
} from "@mantine/core";
import {
  IconCreditCard,
  IconAlertCircle,
  IconCheck,
} from "@tabler/icons-react";
import {
  Elements,
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import {
  loadStripe,
  type StripeCardElementOptions,
  type StripeCardNumberElementOptions,
  type SetupIntent,
  type StripeElementStyle,
} from "@stripe/stripe-js";
import { useApp } from "../../../context/AppContext";
import { useResponsive } from "../../../context/ResponsiveContext";
import type { GetClientPaymentMethodsResponse } from "../../../types/cardOnFile";
import styles from "./AddCardModal.module.css";

const stripePromiseCache = new Map<string, ReturnType<typeof loadStripe>>();

const getStripePromise = (stripeAccountId?: string) => {
  const publishableKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || "";
  const accountId = stripeAccountId?.trim() || "";
  const cacheKey = `${publishableKey}::${accountId}`;

  const existingPromise = stripePromiseCache.get(cacheKey);
  if (existingPromise) {
    return existingPromise;
  }

  const promise = loadStripe(
    publishableKey,
    accountId ? { stripeAccount: accountId } : undefined,
  );
  stripePromiseCache.set(cacheKey, promise);
  return promise;
};

const CARD_ELEMENT_BASE: StripeElementStyle = {
  base: {
    fontSize: "16px",
    lineHeight: "24px",
    color: "#101820",
    fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif',
    fontSmoothing: "antialiased",
    "::placeholder": {
      color: "#aab7c4",
    },
    iconColor: "#36AFC9",
  },
  invalid: {
    color: "#fa5252",
    iconColor: "#fa5252",
  },
};

const CARD_NUMBER_OPTIONS: StripeCardNumberElementOptions = {
  style: CARD_ELEMENT_BASE,
  placeholder: "1234 5678 9012 3456",
};

const CARD_EXPIRY_OPTIONS: StripeCardElementOptions = {
  style: CARD_ELEMENT_BASE,
};

const CARD_CVC_OPTIONS: StripeCardElementOptions = {
  style: CARD_ELEMENT_BASE,
};

interface AddCardModalProps {
  opened: boolean;
  onClose: () => void;
  clientId: string;
  salonId: string;
  stripeAccountId?: string;
  clientName?: string;
  onSuccess?: () => void;
}

interface CardFormProps {
  clientId: string;
  salonId: string;
  clientName?: string;
  onSuccess: () => void;
  onCancel: () => void;
}

async function resolveStripePaymentMethodId(
  setupIntent: SetupIntent | undefined,
): Promise<string | null> {
  const pm = setupIntent?.payment_method;
  if (!pm) return null;
  if (typeof pm === "string") return pm;
  return pm.id ?? null;
}

/** Refetch payment methods until the new Stripe PM appears (eventual consistency). */
async function waitForNewPaymentMethod(
  getClientPaymentMethods: (
    id: string,
  ) => Promise<GetClientPaymentMethodsResponse>,
  clientId: string,
  stripePmId: string,
  maxAttempts = 60,
  delayMs = 100,
): Promise<number | null> {
  for (let i = 0; i < maxAttempts; i += 1) {
    const res = await getClientPaymentMethods(clientId);
    const row = res.paymentMethods.find(
      (p) => p.stripePaymentMethodId === stripePmId,
    );
    if (row) return row.id;
    await new Promise((r) => setTimeout(r, delayMs));
  }
  return null;
}

function CardForm({
  clientId,
  salonId,
  clientName,
  onSuccess,
  onCancel,
}: CardFormProps) {
  const stripe = useStripe();
  const elements = useElements();
  const {
    createSetupIntent,
    getClientPaymentMethods,
    setDefaultClientPaymentMethod,
    setClientPaymentMethodNickname,
  } = useApp();

  const [cardNickname, setCardNickname] = useState("");
  const [cardholderName, setCardholderName] = useState("");
  const [billingZip, setBillingZip] = useState("");
  const [setAsDefault, setSetAsDefault] = useState(false);

  const [elementState, setElementState] = useState({
    cardNumber: { complete: false, error: null as string | null },
    cardExpiry: { complete: false, error: null as string | null },
    cardCvc: { complete: false, error: null as string | null },
  });

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showSuccess, setShowSuccess] = useState(false);

  useEffect(() => {
    if (clientName?.trim()) {
      setCardholderName(clientName.trim());
    } else {
      setCardholderName("");
    }
  }, [clientName]);

  const elementsReady =
    elementState.cardNumber.complete &&
    elementState.cardExpiry.complete &&
    elementState.cardCvc.complete;

  const canSubmit = Boolean(
    stripe && elements && cardholderName.trim() && elementsReady,
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    // This card form is often rendered (via the modal's portal) inside another
    // <form> — e.g. the Edit Client dialog. React propagates synthetic submit
    // events along the component tree through portals, so without this the
    // outer form would also submit (updating the client + closing the dialog)
    // mid-card-flow, and the card would never attach. Keep the card save
    // isolated to this form.
    e.stopPropagation();

    if (!stripe || !elements) {
      setError("Stripe has not loaded yet. Please try again.");
      return;
    }

    const cardNumberElement = elements.getElement(CardNumberElement);
    if (!cardNumberElement) {
      setError("Card fields are not ready. Please refresh and try again.");
      return;
    }

    const zip = billingZip.replace(/\D/g, "");
    if (billingZip.trim() && zip.length !== 5) {
      setError("Billing zip must be 5 digits.");
      return;
    }

    const nicknameTrim = cardNickname.trim();
    if (nicknameTrim.length > 64) {
      setError("Card nickname must be 64 characters or less.");
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const setupIntentResponse = await createSetupIntent({
        clientId,
        salonId,
      });

      /**
       * Billing: Stripe stores `billing_details` on the PaymentMethod (name, postal_code).
       * Metadata: attached to the PaymentMethod in Stripe Dashboard / API (max 50 keys; values max 500 chars).
       * See https://stripe.com/docs/api/payment_methods/object#payment_method_object-metadata
       */
      const { error: stripeError, setupIntent } = await stripe.confirmCardSetup(
        setupIntentResponse.clientSecret,
        {
          payment_method: {
            card: cardNumberElement,
            billing_details: {
              name: cardholderName.trim(),
              ...(zip.length === 5 ? { address: { postal_code: zip } } : {}),
            },
            metadata: {
              client_id: clientId,
              salon_id: salonId,
              source: "bookings_admin_portal",
            },
          },
        },
      );

      if (stripeError) {
        setError(
          stripeError.message || "Failed to save card. Please try again.",
        );
        return;
      }

      if (setupIntent?.status === "succeeded") {
        const stripePmId = await resolveStripePaymentMethodId(setupIntent);
        // Persistence is eventually consistent: Stripe fires a
        // setup_intent.succeeded webhook that async-invokes an enrichment
        // Lambda which writes the client_payment_methods row. GET
        // /payment-methods reads that row, so we must wait for it to land —
        // otherwise the caller's refetch races ahead and the new card only
        // shows after a manual refresh. Always poll (not just when applying a
        // nickname/default), then apply nickname/default if requested.
        if (stripePmId) {
          try {
            const internalId = await waitForNewPaymentMethod(
              getClientPaymentMethods,
              clientId,
              stripePmId,
            );
            if (internalId != null) {
              if (nicknameTrim.length > 0) {
                await setClientPaymentMethodNickname(
                  clientId,
                  internalId,
                  nicknameTrim,
                );
              }
              if (setAsDefault) {
                await setDefaultClientPaymentMethod(clientId, internalId);
              }
            }
          } catch (followUpErr) {
            // Best-effort: a slow/failed webhook must never block the user.
            // We still report success; the card list will reconcile on next load.
            console.warn(
              "Could not confirm payment method after save:",
              followUpErr,
            );
          }
        }

        setShowSuccess(true);
        setTimeout(() => {
          onSuccess();
        }, 1500);
      } else {
        setError("Card setup did not complete. Please try again.");
      }
    } catch (err) {
      console.error("Error saving card:", err);
      setError(
        err instanceof Error
          ? err.message
          : "An unexpected error occurred. Please try again.",
      );
    } finally {
      setIsLoading(false);
    }
  };

  if (showSuccess) {
    return (
      <Stack align="center" gap="md" py="xl">
        <Box className={styles.successIcon}>
          <IconCheck size={48} stroke={2} />
        </Box>
        <Text size="lg" fw={500}>
          Card saved successfully
        </Text>
      </Stack>
    );
  }

  return (
    <form onSubmit={handleSubmit}>
      <Stack gap="md">
        {error && (
          <Alert
            icon={<IconAlertCircle size={16} />}
            color="red"
            variant="light"
          >
            {error}
          </Alert>
        )}

        <TextInput
          label="Card nickname"
          description="Optional — shown in your card list."
          placeholder="e.g. Work card"
          value={cardNickname}
          onChange={(e) => setCardNickname(e.currentTarget.value.slice(0, 64))}
          maxLength={64}
          disabled={isLoading}
        />

        <TextInput
          label="Cardholder Name"
          placeholder="John Doe"
          value={cardholderName}
          onChange={(e) => setCardholderName(e.currentTarget.value)}
          required
          disabled={isLoading}
        />

        <div>
          <Text size="sm" fw={600} mb={6} className={styles.fieldLabel}>
            Card Number
          </Text>
          <Box className={styles.stripeFieldWrap}>
            <CardNumberElement
              options={CARD_NUMBER_OPTIONS}
              onChange={(ev) => {
                setElementState((s) => ({
                  ...s,
                  cardNumber: {
                    complete: ev.complete,
                    error: ev.error?.message ?? null,
                  },
                }));
                if (ev.error) setError(ev.error.message);
              }}
            />
          </Box>
        </div>

        {/*
          Use flex Group instead of Grid: Mantine Grid negative margins + scroll lock
          can interfere with Stripe iframe hit targets; flex keeps fields fully clickable.
        */}
        <Group grow align="flex-start" gap="md" wrap="wrap">
          <Box className={styles.expiryCvcField} miw={0}>
            <Text size="sm" fw={600} mb={6} className={styles.fieldLabel}>
              Expiration
            </Text>
            <Box className={styles.stripeFieldWrap}>
              <CardExpiryElement
                options={CARD_EXPIRY_OPTIONS}
                onChange={(ev) => {
                  setElementState((s) => ({
                    ...s,
                    cardExpiry: {
                      complete: ev.complete,
                      error: ev.error?.message ?? null,
                    },
                  }));
                  if (ev.error) setError(ev.error.message);
                }}
              />
            </Box>
          </Box>
          <Box className={styles.expiryCvcField} miw={0}>
            <Text size="sm" fw={600} mb={6} className={styles.fieldLabel}>
              CVV
            </Text>
            <Box className={styles.stripeFieldWrap}>
              <CardCvcElement
                options={CARD_CVC_OPTIONS}
                onChange={(ev) => {
                  setElementState((s) => ({
                    ...s,
                    cardCvc: {
                      complete: ev.complete,
                      error: ev.error?.message ?? null,
                    },
                  }));
                  if (ev.error) setError(ev.error.message);
                }}
              />
            </Box>
          </Box>
        </Group>

        <TextInput
          label="Billing Zip Code"
          placeholder="12345"
          value={billingZip}
          onChange={(e) =>
            setBillingZip(e.currentTarget.value.replace(/\D/g, "").slice(0, 5))
          }
          disabled={isLoading}
        />

        <Checkbox
          label="Set as default card"
          checked={setAsDefault}
          onChange={(e) => setSetAsDefault(e.currentTarget.checked)}
          disabled={isLoading}
          color="teal"
        />

        <Text size="xs" c="dimmed">
          Card numbers are tokenized by Stripe. We never store full card data on
          our servers.
        </Text>

        <Group justify="flex-end" gap="sm" mt="md">
          <Button variant="default" onClick={onCancel} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            type="submit"
            className={styles.saveButton}
            loading={isLoading}
            disabled={!canSubmit}
            leftSection={<IconCreditCard size={18} />}
          >
            Save
          </Button>
        </Group>
      </Stack>
    </form>
  );
}

export default function AddCardModal({
  opened,
  onClose,
  clientId,
  salonId,
  stripeAccountId,
  clientName,
  onSuccess,
}: AddCardModalProps) {
  const { isMobile } = useResponsive();
  const [key, setKey] = useState(0);

  useEffect(() => {
    if (opened) {
      setKey((prev) => prev + 1);
    }
  }, [opened]);

  const handleSuccess = () => {
    onSuccess?.();
    onClose();
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={
        <Text fw={700} size="lg" className={styles.modalTitle}>
          Add Card
        </Text>
      }
      size="lg"
      fullScreen={isMobile}
      centered
      closeOnClickOutside={false}
      /**
       * Stripe Card Elements render in iframes. Mantine’s default focus trap
       * keeps focus inside the modal DOM and prevents those iframes from
       * receiving focus/clicks (expiry + CVC often feel “dead” while number works).
       */
      trapFocus={false}
      /**
       * `react-remove-scroll` (scroll lock) can block pointer events to nested
       * iframes in some browsers — users can’t click Expiry/CVV at all.
       */
      lockScroll={false}
      classNames={{ header: styles.modalHeader, body: styles.modalBody }}
    >
      {clientName && (
        <Text size="sm" c="dimmed" mb="md">
          Adding card for{" "}
          <Text span fw={500}>
            {clientName}
          </Text>
        </Text>
      )}

      <Elements stripe={getStripePromise(stripeAccountId)} key={key}>
        <CardForm
          clientId={clientId}
          salonId={salonId}
          clientName={clientName}
          onSuccess={handleSuccess}
          onCancel={onClose}
        />
      </Elements>
    </Modal>
  );
}
